/**
 * clase de utilidad para validar campos en formularios
 */
import useRegexConstants from '@/helpers/useRegexConstants';

export default function useFormValidator() {
  const { REGEX_VALID_TELEFONO, REGEX_VALID_EMAIL } = useRegexConstants();

  /**
   * Valida el input dadas las condiciones que se le pasen como parámetros
   *
   * @param input valor a validar
   * @param type  tipo del input a validar (email, text, tel)
   * @param required input es requerido o no
   * @param maxLength maxima long permitida del campo input
   * @param minLength minima long permitida del campo input
   * @param regex expresion regex que se evaluará contra el campo input , ejemplo: /^[0-9+-]+$/
   * @returns {*[]} Array de errores
   */
  function validateInput(input, type, required = false, maxLength = null, minLength = null, regex = null) {
    const validationErrors = [];
    const requiredError = validateRequired(input, required);
    // Si es campo requerido, y no se cumple la validacion, no seguimos con el resto de validaciones
    if (requiredError) {
      validationErrors.push(requiredError);
    } else {
      validationErrors.push(validateType(input, type));
      validationErrors.push(validateMaxLength(input, maxLength));
      validationErrors.push(validateMinLength(input, minLength));
      validationErrors.push(validateRegex(input, regex));
    }
    // Filtra elementos no nulos dentro del array
    return validationErrors.filter(elemnt => elemnt);
  }

  /**
   * Valida si el campo existe en caso de ser requerido
   *
   * @param input valor a validar
   * @param required booleano que indica si es requerido
   * @returns {string} error en caso de no cumplir la validacion
   */
  function validateRequired(input, required) {
    const CAMPO_REQUERIDO = 'Rellene el campo requerido';
    if (required && (!input || input === '')) {
      return CAMPO_REQUERIDO;
    }
  }

  /**
   * Valida un tipo de input que se pase como parámetro
   *
   * @param input valor a validar
   * @param type  tipo del campo, segun el tipo se pasan unas u otras validaciones
   * @returns {string} String con el error en caso de existir
   */
  function validateType(input, type) {
    const validator = validatorTypeFactory(type);
    if (validator) {
      return validator(input);
    }
  }

  /**
   * Devuelve el tipo de validador en funcion del tipo de input
   *
   * Soportados:
   *  - email     -> direcciones email
   *  - tel       -> telefono
   *  - cualquier otro -> no hay validacion especificas
   *
   * @param type
   * @returns {(function(*=): (string|undefined))|*}
   */
  function validatorTypeFactory(type) {
    if (type === 'email') {
      return validateEmail;
    } else if (type === 'tel') {
      return validateTel;
    }
  }

  /**
   * Valida el tipo de input "email"
   *
   * @param email valor a validar
   * @returns {string} error
   */
  const validateEmail = email => {
    const EMAIL_ERROR_VALIDATION = 'El email no tiene un formato válido';
    if (!String(email).toLowerCase().match(REGEX_VALID_EMAIL)) {
      return EMAIL_ERROR_VALIDATION;
    }
  };

  /**
   * Valida tipo de input "tel"
   *
   * @param telefono valor a validar
   * @returns {string} error
   */
  const validateTel = telefono => {
    const TELEFONO_ERROR_VALIDATION = 'El teléfono no tiene un formato válido';
    if (!String(telefono).match(REGEX_VALID_TELEFONO)) {
      return TELEFONO_ERROR_VALIDATION;
    }
  };

  /**
   * Valida si el input cumple la longitud máxima indicada
   *
   * @param input valor a validar
   * @param maxLength máxima longitud de la cadena
   * @returns {string} error
   */
  function validateMaxLength(input, maxLength) {
    const MAX_LENGHT = 'El campo debe tener como máximo ' + maxLength + ' carácteres';
    if (maxLength && input && input.length > maxLength) {
      return MAX_LENGHT;
    }
  }

  /**
   * Valida si el input cumple la longitud mínima indicada
   *
   * @param input valor a validar
   * @param minLength minima longitud de la cadena
   * @returns {string} error
   */
  function validateMinLength(input, minLength) {
    const MIN_LENGHT = 'El campo debe tener al menos ' + minLength + ' carácteres';
    if (minLength && input && input.length < minLength) {
      return MIN_LENGHT;
    }
  }

  /**
   * Valida que el input cumple la regex indicada
   *
   * @param input valor a validar
   * @param regex regex contra el que validar
   * @returns {string} error
   */
  const validateRegex = (input, regex) => {
    const REGEX_ERROR_VALIDATION = 'El campo no tiene un formato válido';
    if (regex && input && !input.match(regex)) {
      return REGEX_ERROR_VALIDATION;
    }
  };

  return {
    validateInput,
  };
}
